import { ComponentProps, ReactNode } from 'react';

import styles from '~/modules/game-engine/drawing/styles';
import { Achievement } from '~/modules/user-profiles/achievements';
import { GRADE_COLOURS, gradeColour } from '~/modules/user-profiles/grade-colours';
import { HIGHEST_SINGABLE_PITCH, LOWEST_SINGABLE_PITCH, pitchToNoteName } from '~/modules/user-profiles/pitch';
import { PlayOutcome } from '~/modules/user-profiles/play-summary';
import { Grade, GRADES } from '~/modules/user-profiles/profile-stats';
import { profileInitial } from '~/modules/user-profiles/profiles';
import { cn } from '~/utils/cn';

export const Panel = ({ className, children, ...props }: ComponentProps<'div'>) => (
  <div
    className={cn(
      'flex min-w-0 flex-col gap-1.5 rounded-xl border border-white/10 bg-black/45 px-[1.375rem] py-[1.125rem]',
      className,
    )}
    {...props}>
    {children}
  </div>
);

export const PanelTitle = ({ children }: { children: ReactNode }) => (
  <span className="typography text-[1.375rem] font-bold text-white/85">{children}</span>
);

export const PanelLabel = ({ children }: { children: ReactNode }) => (
  <span className="text-sm tracking-[0.08em] text-white/45 uppercase">{children}</span>
);

export const PanelEmpty = ({ children, ...props }: ComponentProps<'div'>) => (
  <div className="flex flex-1 items-center justify-center py-8 text-[1.1875rem] text-white/35" {...props}>
    {children ?? 'No information available'}
  </div>
);

/** `null` reads as an em dash: a stat that was never measured must not show a zero standing in. */
export const StatCell = ({ label, value, note }: { label: string; value: string | null; note?: string }) => (
  <div className="flex flex-col items-center gap-0.5 px-2 text-center">
    <span
      className={cn('typography text-[2rem] leading-[1.2] font-bold tabular-nums', value === null && 'text-white/35')}>
      {value ?? '—'}
    </span>
    <span className="text-[0.9375rem] text-white/50">{label}</span>
    {note && <span className="text-[0.8125rem] text-white/35">{note}</span>}
  </div>
);

export const RankRing = ({ grade }: { grade: Grade }) => (
  <div
    className="flex size-24 shrink-0 items-center justify-center rounded-full text-[3.5rem] leading-none font-black"
    style={{
      color: GRADE_COLOURS[grade],
      background: gradeColour(grade, '1a'),
      border: `3px solid ${GRADE_COLOURS[grade]}`,
      boxShadow: `0 0 1.75rem ${gradeColour(grade, '33')}`,
    }}>
    {grade}
  </div>
);

/**
 * `large` is the pill a history row leads with, where the grade is the first thing read and has to
 * carry at TV distance; `small` is the one that trails a profile row, beside a name that leads
 * instead. In `rem` throughout, so all three follow the root scale (see `index.css`).
 */
const GRADE_PILL_SIZES = {
  small: 'h-[1.625rem] min-w-[1.75rem] rounded-md px-1.5 text-[1rem]',
  regular: 'h-[1.75rem] min-w-[1.875rem] rounded-md px-2 text-[1.125rem]',
  large: 'h-[2.375rem] min-w-[2.375rem] rounded-lg px-2 text-[1.375rem]',
} as const;

export const GradePill = ({ grade, size = 'regular' }: { grade: Grade; size?: keyof typeof GRADE_PILL_SIZES }) => (
  <span
    className={cn('flex shrink-0 items-center justify-center font-black', GRADE_PILL_SIZES[size])}
    style={{
      color: GRADE_COLOURS[grade],
      background: gradeColour(grade, '1f'),
      border: `1px solid ${gradeColour(grade, '66')}`,
    }}>
    {grade}
  </span>
);

/** Bars are relative to the commonest grade, with a floor so a grade nobody reached still reads. */
export const GradeDistribution = ({ distribution }: { distribution: Record<Grade, number> }) => {
  const most = Math.max(...GRADES.map(({ grade }) => distribution[grade]), 1);

  return (
    // `mt-auto`, not a fixed margin: this panel is stretched to the height of Recent plays beside
    // it, and the chart summarises the list above, so the foot of the panel is where it belongs -
    // the same reason the History card pins its footer that way rather than letting it float.
    <div className="mt-auto flex items-end gap-2 pt-3" data-test="profile-grade-distribution">
      {/* Worst to best, left to right, so the chart reads as climbing towards S. */}
      {[...GRADES].reverse().map(({ grade }) => {
        const count = distribution[grade];
        const colour = count === 0 ? undefined : { color: GRADE_COLOURS[grade] };

        return (
          <div key={grade} className="flex flex-1 flex-col items-center gap-1">
            <span
              className={cn('text-[0.9375rem] font-bold tabular-nums', count === 0 && 'text-white/25')}
              style={colour}>
              {count}
            </span>
            <span
              className={cn('w-full rounded-[0.1875rem]', count === 0 && 'bg-white/12')}
              style={{
                // In `rem` so the chart follows the root scale like everything around it. The floor
                // is what keeps a grade nobody reached visible as a bar rather than as nothing.
                height: `${(4 + (count / most) * 34) / 16}rem`,
                ...(count === 0 ? {} : { background: GRADE_COLOURS[grade] }),
              }}
            />
            <span className={cn('text-sm font-black', count === 0 && 'text-white/25')} style={colour}>
              {grade}
            </span>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Who sang the round. The position in the list is the slot they sang on - which is all a stored
 * play says about who was where, and in co-op is all that survives of the roster at all.
 */
export const SingerInitials = ({ names }: { names: string[] }) => (
  <span className="flex items-center gap-1">
    {names.map((name, index) => (
      <span
        key={`${name}-${index}`}
        title={name}
        className="ph-no-capture flex size-[1.625rem] shrink-0 items-center justify-center rounded-full text-[0.875rem] font-bold text-black"
        style={{ background: styles.colors.players[index]?.text ?? styles.colors.text.inactive }}>
        {profileInitial(name)}
      </span>
    ))}
  </span>
);

// Won and Lost take the ladder's A and E, so green and red mean the same here as on a grade.
const OUTCOME_STYLES: Record<PlayOutcome, string> = {
  'Co-op': 'border-white/15 bg-slate-600/40 font-semibold text-white/80',
  Solo: 'border-white/15 bg-slate-600/40 font-semibold text-white/80',
  Draw: 'border-white/15 bg-slate-600/40 font-semibold text-white/80',
  Won: 'border-[#88da20]/40 bg-[#88da20]/12 font-bold text-[#88da20]',
  Lost: 'border-[#ff5a5a]/40 bg-[#ff5a5a]/12 font-bold text-[#ff5a5a]',
};

export const OutcomeChip = ({ outcome }: { outcome: PlayOutcome }) => (
  <span
    className={cn(
      'flex h-6 shrink-0 items-center rounded-sm border px-2 text-[0.8125rem] uppercase',
      OUTCOME_STYLES[outcome],
    )}
    data-test={`play-outcome-${outcome.toLowerCase()}`}>
    {outcome}
  </span>
);

/**
 * The span, and how much of a voice it covers. Measured against the range the detector itself
 * accepts, so the bar means the same thing for every profile.
 */
export const VocalRange = ({ range }: { range?: { low: number; high: number } }) => {
  const singable = HIGHEST_SINGABLE_PITCH - LOWEST_SINGABLE_PITCH;
  const share = (pitch: number) => Math.max(0, Math.min(1, (pitch - LOWEST_SINGABLE_PITCH) / singable));

  return (
    <div className="flex flex-col gap-1.5">
      <PanelLabel>Vocal range</PanelLabel>
      <div className="flex items-center gap-3">
        {range ? (
          <span className="stroke-text text-[1.5rem] font-bold whitespace-nowrap" data-test="profile-vocal-range">
            {pitchToNoteName(range.low)} – {pitchToNoteName(range.high)}
          </span>
        ) : (
          <span className="text-[1.5rem] font-bold whitespace-nowrap text-white/35" data-test="profile-vocal-range">
            Not measured
          </span>
        )}
        <span className="relative inline-block h-[0.625rem] w-[9.375rem] shrink-0 rounded-full bg-white/12">
          {range && (
            <span
              className="bg-active absolute inset-y-0 rounded-full"
              style={{ left: `${share(range.low) * 100}%`, width: `${(share(range.high) - share(range.low)) * 100}%` }}
            />
          )}
        </span>
      </div>
    </div>
  );
};

export const AchievementTile = ({ achievement }: { achievement: Achievement }) => (
  <div
    className={cn(
      'flex h-16 items-center gap-2.5 rounded-lg border px-3',
      achievement.unlocked ? 'border-active/40 bg-active/10' : 'border-white/10 bg-black/35 opacity-55',
    )}
    title={achievement.description}
    data-test={`achievement-${achievement.unlocked ? 'unlocked' : 'locked'}`}>
    <span className={cn('text-[1.375rem]', !achievement.unlocked && 'grayscale')}>{achievement.icon}</span>
    <span className="flex min-w-0 flex-col">
      <span className="truncate text-[0.9375rem] font-bold text-white">{achievement.name}</span>
      <span className="truncate text-[0.8125rem] text-white/50">
        {achievement.unlocked ? achievement.description : `${achievement.progress} / ${achievement.target}`}
      </span>
    </span>
  </div>
);
